import { createWebHistory, createRouter } from "vue-router";
import login from "@/pages/auth/login.vue";
import register from "@/pages/auth/register.vue";
import resetPassword from "@/pages/auth/reset-password.vue";
import forgotPassword from "@/pages/auth/forgot-password.vue";
import verifyEmail from "@/pages/auth/verify-email.vue";
import ItemDetail from "@/pages/explore/ItemDetails.vue";
import CreatorProfile from "@/pages/user/DataStudio.vue";
import CreatorProfileEdit from "@/pages/user/creator-profile-edit.vue";
import SellerProfile from "@/pages/user/seller-profile.vue";
import DatasetCreate from "@/pages/dataset/DatasetCreate.vue";
import ListingCreate from "@/pages/listing/ListingCreate.vue";
import Explore from "@/pages/explore/Explore.vue";
import CheckoutConfirmation from "@/pages/explore/checkout-confirmation.vue";
import Purchases from "@/pages/user/purchases.vue";
import { useStorage } from "@/composables/use-storage";
import { useSystemSettings } from "@/composables/use-system-settings";
import StripeDashboard from "@/pages/admin/stripe-dashboard.vue";
import AccountCreation from "@/pages/user/AccountCreation.vue";
import DaoAccountCreation from "@/pages/user/DaoAccountCreation.vue";
import ListUserGroup from "@/pages/admin/user-group/ListUserGroup.vue";
import SaveUserGroup from "@/pages/admin/user-group/SaveUserGroup.vue";
import ListingDatasets from "@/pages/admin/listings-datasets/ListingDatasets.vue";
import SystemSettings from "@/pages/admin/system-settings.vue";
import StripeConnect from "@/pages/stripe/StripeConnect.vue";
import StripeConnectRedirect from "@/pages/stripe/StripeConnectRedirect.vue";
import Status from "@/pages/Status.vue";
import RFPCreate from "@/pages/user/NewRFP.vue";
import Maintenance from "@/pages/status/503.vue";
import RFPArbitration from "@/pages/admin/rfps/RFPArbitration.vue";
import RFPConfirmation from "@/pages/RFPConfirmation.vue";
import SendUsAMessage from "@/pages/email/send-us-a-message.vue";
import ListFiles from "@/pages/admin/files/ListFiles.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import AcceptFiles from "@/pages/admin/files/AcceptFiles.vue";
import UsersView from "@/pages/admin/users/UsersView.vue";
import AdminLayout from "@/layouts/AdminLayout.vue";
import AdminDashboard from "@/pages/admin/AdminDashboard.vue";
import ListingsView from "@/pages/admin/listings/ListingsView.vue";
import ListBadgesView from "@/pages/admin/user-badges/ListBadgesView.vue";
import SalesCredentials from "@/pages/admin/SalesCredentials.vue";

import { useAnalytics } from "@/composables/use-analytics";
import ListTags from "@/pages/admin/listings/ListTags.vue";
import ListTagTypes from "@/pages/admin/listings/ListTagTypes.vue";
import RFPsView from "@/pages/admin/rfps/RfpsView.vue";
import FlagsView from "@/pages/admin/flags/FlagsView.vue";
import RequestsView from "@/pages/admin/requests/RequestsView.vue";
import PublicationRequestsView from "@/pages/admin/requests/PublicationRequestsView.vue";
import DatasetsView from "@/pages/admin/datasets/DatasetsView.vue";

import DataLicenseSave from "@/pages/admin/listings/data-licenses/DataLicenseSave.vue";
import DataLicensesList from "@/pages/admin/listings/data-licenses/DataLicensesList.vue";

import EvalRunsQueueDashboard from "@/pages/admin/actions/EvalRunsQueueDashboard.vue";
import CacheManagement from "@/pages/admin/actions/CacheManagement.vue";
import EvalLLMsView from "@/pages/admin/eval-llms/EvalLLMsView.vue";
import EvalAgentsView from "@/pages/admin/eval-agents/EvalAgentsView.vue";
import PaymentMethodsView from "@/pages/admin/listings/payment-methods/PaymentMethodsView.vue";
import EvalDatasetsView from "@/pages/eval/eval-datasets/EvalDatasetsView.vue";

const { pageview } = useAnalytics();


function isAuthenticated() {
  const localToken = localStorage.getItem('authToken');
  const sessionToken = sessionStorage.getItem('authToken');
  return localToken !== null || sessionToken !== null;
}

function isAdmin() {
  const { userInfo } = useStorage();
  return userInfo.value?.is_admin === true;
}

// function to load system settings
const loadSystemSettingsGuard = async () => {
  try {
    const { loadSystemSettings } = useSystemSettings();
    await loadSystemSettings();
    return true;
  } catch (error) {
    console.error('Error loading system settings:', error);
    return false;
  }
};

const { hasPermission } = useStorage();

const routes = [
  {
    path: '/',
    name: 'base',
    component: BaseLayout,
    children: [
      {
        path: '/',
        redirect: '/explore'
      },
      {
        path: '/status',
        name: 'status',
        component: Status,
      },
      {
        path: '/login',
        name: 'login',
        component: login,
        meta: { hideNavbar: true }
      },
      {
        path: '/register',
        name: 'register',
        component: register,
        meta: { hideNavbar: true }
      },
      {
        path: '/reset-password',
        name: 'reset-password',
        component: resetPassword,
        meta: { hideNavbar: true }
      },
      {
        path: '/forgot-password',
        name: 'forgot-password',
        component: forgotPassword,
        meta: { hideNavbar: true },
        props: true
      },
      {
        path: '/auth/verify-email/:token',
        name: 'verify-email',
        component: verifyEmail,
        meta: { hideNavbar: true },
      },
      {
        path: '/status/503',
        name: '503',
        component: Maintenance,
      },
      {
        path: "/explore",
        name: "explore",
        component: Explore,
      },
      {
        path: '/send-us-a-message',
        name: 'send-us-a-message',
        component: SendUsAMessage,
      },
      {
        path: "/item-detail/:id",
        name: "item-detail",
        component: ItemDetail,
      },
      {
        path: "/creator-profile",
        name: "creator-profile",
        component: CreatorProfile,
        meta: { requiresAuth: true }
      },
      {
        path: "/creator-profile-edit",
        name: "creator-profile-edit",
        component: CreatorProfileEdit,
        meta: { requiresAuth: true }
      },
      {
        path: "/seller-profile/:id",
        name: "seller-profile",
        component: SellerProfile,
        meta: { requiresAuth: true }
      },
      {
        path: "/dataset-create",
        name: "dataset-create",
        component: DatasetCreate,
        meta: { requiresAuth: true, permissions: ['datasets'] }
      },
      {
        path: "/dataset-edit/:id",
        name: "dataset-edit",
        component: DatasetCreate,
        meta: { requiresAuth: true, permissions: ['datasets'] },
        props: true
      },
      {
        path: "/listing-create",
        name: "listing-create",
        component: ListingCreate,
        meta: { requiresAuth: true, permissions: ['listings'] }
      },
      {
        path: "/listing-edit",
        name: "listing-edit",
        component: ListingCreate,
        meta: { requiresAuth: true, permissions: ['listings'] },
        props: true
      },
      {
        path: "/dataset-listing-create/:datasetId",
        name: "dataset-listing-create",
        component: ListingCreate,
        meta: { requiresAuth: true, permissions: ['listings'] },
        props: true
      },
      {
        path: "/checkout-confirmation",
        name: "checkout-confirmation",
        component: CheckoutConfirmation,
        meta: { requiresAuth: true }
      },
      {
        path: "/purchases",
        name: "purchases",
        component: Purchases,
        meta: { requiresAuth: true }
      },
      {
        path: "/account-creation",
        name: "account-creation",
        component: AccountCreation,
        state: true,
        meta: { hideNavbar: true }
      },
      {
        path: "/dao-account-creation",
        name: "dao-account-creation",
        component: DaoAccountCreation
      },
      {
        path: "/stripe/stripe-redirect",
        name: "stripe-redirect",
        component: StripeConnectRedirect,
      },
      {
        path: "/stripe/connect",
        name: "stripe-connect",
        component: StripeConnect,
      },
      {
        path: "/rfp/create",
        name: "rfp-create",
        component: RFPCreate,
        meta: { requiresAuth: true, permissions: ['buy'] }
      },
      {
        path: "/rfp/edit/:id",
        name: "rfp-edit",
        component: RFPCreate,
        meta: { requiresAuth: true, permissions: ['buy'] }
      },
      {
        path: "/rfp-confirmation",
        name: "rfp-confirmation",
        component: RFPConfirmation,
        meta: { requiresAuth: true }
      },
      {
        path: "/eval-datasets",
        name: "eval-datasets",
        component: EvalDatasetsView,
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/backoffice',
    name: 'backoffice',
    component: AdminLayout,
    children: [
      {
        path: "/backoffice",
        name: "backoffice-dashboard",
        component: AdminDashboard,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/system-settings",
        name: "system-settings",
        component: SystemSettings,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/listings",
        name: "listings-list",
        component: ListingsView,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/listings-tags",
        name: "listings-tags",
        component: ListTags,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/eval-llms",
        name: "eval-llms",
        component: EvalLLMsView,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/eval-agents",
        name: "eval-agents",
        component: EvalAgentsView,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/licenses",
        name: "licenses",
        component: DataLicensesList,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/payment-methods",
        name: "payment-methods",
        component: PaymentMethodsView,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/licenses/save",
        name: "license-save",
        component: DataLicenseSave,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/rfps",
        name: "rfps",
        component: RFPsView,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/rfps-arbitration",
        name: "rfps-arbitration",
        component: RFPArbitration,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      // {
      //   path: "/admin/bids",
      //   name: "bids",
      //   component: BidsView,
      //   meta: { requiresAuth: true, requiresAdmin: true }
      // },
      {
        path: "/backoffice/listings-tag-types",
        name: "listings-tag-types",
        component: ListTagTypes,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/stripe-dashboard",
        name: "stripe-dashboard",
        component: StripeDashboard,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/groups",
        name: "groups",
        component: ListUserGroup,
        meta: { requiresAuth: true, permissions: ['group-manager', 'group-set-users', 'group-flags'] }
      },
      {
        path: "/backoffice/flags",
        name: "flags",
        component: FlagsView,
        meta: { requiresAuth: true, requiresAdmin: true, permissions: ['group-flags'] }
      },
      {
        path: "/backoffice/users",
        name: "users",
        component: UsersView,
        meta: { requiresAuth: true, permissions: ['group-manager', 'group-set-users', 'group-flags'] }
      },
      {
        path: "/backoffice/users/badges",
        name: "users-badges",
        component: ListBadgesView,
        meta: { requiresAuth: true, requiresAdmin: true  }
      },
      {
        path: "/backoffice/groups/save",
        name: "groups-save",
        component: SaveUserGroup,
        meta: { requiresAuth: true, permissions: ['group-manager'] }
      },
      {
        path: "/backoffice/groups/save/:id",
        name: "groups-edit",
        component: SaveUserGroup,
        meta: { requiresAuth: true, permissions: ['group-manager'] },
        props: true
      },
      {
        path: "/backoffice/requests",
        name: "requests",
        component: RequestsView,
        meta: { requiresAuth: true, permissions: ['group-manager'] }
      },
      {
        path: "/backoffice/publication-requests",
        name: "publication-requests",
        component: PublicationRequestsView,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/listings-datasets",
        name: "listings-datasets",
        component: ListingDatasets,
        meta: { requiresAuth: true, permissions: ['tags-manager'] }
      },
      {
        path: "/backoffice/datasets",
        name: "datasets",
        component: DatasetsView,
        meta: { requiresAuth: true, permissions: ['datasets'] }
      },
      {
        path: "/backoffice/files",
        name: "files",
        component: ListFiles,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/files/accept",
        name: "files-accept",
        component: AcceptFiles,
        meta: { requiresAuth: true, permissions: ['datasets'] }
      },
      {
        path: "/backoffice/sales/credentials",
        name: "sales-credentials",
        component: SalesCredentials,
        meta: { requiresAuth: true, permissions: ['tags-manager'] }
      },
      {
        path: "/backoffice/eval-runs-queue",
        name: "eval-runs-queue",
        component: EvalRunsQueueDashboard,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "/backoffice/cache-management",
        name: "cache-management",
        component: CacheManagement,
        meta: { requiresAuth: true, requiresAdmin: true }
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const verifyHasPermissions = (permissions) => {
  if (!Array.isArray(permissions)) {
    permissions = [permissions];
  }
  return permissions.some(permission => hasPermission(permission));
};

router.beforeEach(async (to, from, next) => {
  await loadSystemSettingsGuard();
  if(!to.path.startsWith('/backoffice')) {
    pageview(window.location.href);
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated()) {
      next({ name: 'login' }); // Redirect to login if not authenticated
    } else if (to.matched.some(record => record.meta.requiresAdmin) && !isAdmin()) {
      next({ name: 'explore' }); // Redirect to explore if not admin
    } else if (to.matched.some(record => record.meta.permissions && !verifyHasPermissions(record.meta.permissions)) ) {
      next({ name: 'explore' }); // Redirect to explore if not has permission
    } else if (to.name === 'reset-password' || to.name === 'forgot-password' || to.name === 'verify-email') {
      next(); // Proceed to the requested page
    } else {
      next(); // Proceed to the requested page
    }
  } else {
    next(); 
  }
});

export default router;
